import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { NotifyResult } from '@pace/contracts';
import { Icon } from '@pace/ui';
import { useI18n } from '../i18n';
import { api, ApiError, isNetworkError } from '../lib/api';
import { useToast } from './Toasts';

/**
 * Reach out to the people who own a step: one tap emails them where it stands (a dummy
 * email for now — logged on the server and kept in the step's history).
 */
export function NotifyButton({
  projectId,
  stepId,
  who,
  message,
  compact,
  onSent,
}: {
  projectId: string;
  stepId: string;
  /** Name shown on the button (the owner or department). */
  who?: string;
  message?: string;
  /** Icon-only button for dense lists. */
  compact?: boolean;
  onSent?: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const send = useMutation({
    mutationFn: () =>
      api<NotifyResult>(`/steps/${stepId}/notify`, {
        body: message?.trim() ? { message: message.trim() } : {},
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['history', projectId] });
      const names = r.recipients.map((x) => x.name).join(', ');
      toast({
        message: `${t('notify.sent', { names })}${r.dummy ? ` ${t('notify.dummy')}` : ''}`,
        durationMs: 6_000,
      });
      onSent?.();
    },
    onError: (e: Error) => {
      const code = e instanceof ApiError ? e.code : isNetworkError(e) ? 'offline' : '';
      toast({
        message:
          code === 'offline'
            ? t('notify.offline')
            : code === 'notify_recently'
              ? t('notify.recently')
              : code === 'no_recipient'
                ? t('notify.nobody')
                : t('common.somethingWrong'),
        tone: 'error',
      });
    },
  });
  const label = who ? t('notify.to', { name: who }) : t('notify.button');
  if (compact)
    return (
      <button
        type="button"
        className="icon-btn"
        aria-label={label}
        title={label}
        disabled={send.isPending}
        onClick={() => send.mutate()}
      >
        <Icon name="mail" />
      </button>
    );
  return (
    <button
      type="button"
      className="btn btn--line btn--sm"
      disabled={send.isPending}
      onClick={() => send.mutate()}
    >
      <Icon name="mail" />
      {label}
    </button>
  );
}
